import React, { useState } from 'react';
import { X, Heart, Send, Users, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

const ShareMyCBCModal = ({ isOpen, onClose, onDismiss, showDismissOption = true }) => {
  const [friendName, setFriendName] = useState('');
  const [friendEmail, setFriendEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!friendName.trim() || !friendEmail.trim()) {
      toast.error('Please enter your friend\'s name and email');
      return;
    }

    setSending(true);
    try {
      await api.post('/api/share/invite', {
        friend_name: friendName.trim(),
        friend_email: friendEmail.trim(),
        personal_message: personalMessage.trim() || null
      });
      
      setSent(true);
      toast.success(`Invitation sent to ${friendName}!`);
      
      // Reset form after delay
      setTimeout(() => {
        setSent(false);
        setFriendName('');
        setFriendEmail('');
        setPersonalMessage('');
        onClose();
      }, 2000);
      
    } catch (error) {
      toast.error('Failed to send invitation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await api.post('/api/share/dismiss-prompt', { dismissed: true });
      onDismiss?.();
      onClose();
    } catch (error) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-600 transition-colors z-10"
        >
          <X className="h-5 w-5" />
        </button>

        {sent ? (
          /* Success State */
          <div className="p-6 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="h-7 w-7 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Invitation Sent!</h3>
            <p className="text-slate-600 text-sm">
              {friendName} will receive an invitation to join our community.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-4 pb-5 rounded-t-2xl border-b border-teal-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <Users className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Grow the Community</h2>
                  <p className="text-xs text-slate-600">Help someone on their CLL/SLL journey</p>
                </div>
              </div>
              
              <p className="text-slate-700 text-xs leading-relaxed">
                Every person you invite strengthens our community. Together, we're helping each other track, learn, and navigate CLL/SLL.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Their name
                </label>
                <input
                  type="text"
                  value={friendName}
                  onChange={(e) => setFriendName(e.target.value)}
                  placeholder="John"
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Their email
                </label>
                <input
                  type="email"
                  value={friendEmail}
                  onChange={(e) => setFriendEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Add a personal note <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  placeholder="I've joined this community to track my bloods and thought you might find it helpful too..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none text-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full h-11 gap-2"
              >
                {sending ? (
                  'Sending...'
                ) : (
                  <>
                    <Heart className="h-4 w-4" />
                    Invite to the Community
                  </>
                )}
              </Button>

              {showDismissOption && (
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors py-1"
                >
                  Maybe later
                </button>
              )}
            </form>

            {/* Footer note */}
            <div className="px-4 pb-4">
              <p className="text-xs text-slate-400 text-center">
                <Heart className="h-3 w-3 inline mr-1 text-rose-400" />
                We'll only send one invitation — no spam, ever.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareMyCBCModal;
